import { useRef, useCallback, useEffect, useContext } from 'react';

import { FlowsFormSlideContext } from '../../../context/flowsFormSlide';

import type { RefObject } from 'react';

import type { IUseActiveElement } from '../types';


/** 
 * A hook to display 1 of multiple elements at a time.
 * Each item is faded in and out and then set to display NONE if not active.
 */

function useActiveElement(): IUseActiveElement {
  
  const { slide, setSlide } = useContext(FlowsFormSlideContext);

  // initialize an empty array for refs
  const refs = useRef<RefObject<HTMLDivElement>[]>([]);

  // create a callback ref to dynamically add refs
  const setRef = useCallback((el: HTMLDivElement | null, index: number) => {
    if (el && !refs.current[index]) {
      refs.current[index] = { current: el } as RefObject<HTMLDivElement>;
    }
  }, []);

  // default them all to display none;
  useEffect(() => {
    refs.current.forEach((ref, index) => {
      if (ref.current && slide !== index) {
        ref.current.style.display = 'none';
        ref.current.style.zIndex = '0';
      }
    });
  }, [slide, refs]);

  const fadeElement = useCallback(
    (index: number, isVisible: boolean) => {
      return new Promise<void>((resolve) => {
        const ref = refs.current[index];
        const element = ref.current;
        if (!element) {
          resolve();
          return;
        }
        
        // Set the element to display block and z-index to 2
        element.style.display = 'block';
        element.style.zIndex = '2';

        element.animate(
          [
            { opacity: isVisible ? 0 : 1 },
            { opacity: isVisible ? 1 : 0 },
          ],
          {
            duration: 250,
            fill: 'both',
          },
        ).onfinish = () => {
          if (!isVisible) { // if not visible, set to display none and z-index to 0
            element.style.display = 'none';
            element.style.zIndex = '0';
          }
          resolve();
        };
      });
    },
    [],
  );
  
  // This is the function called to change out an element
  const showElement = useCallback(
    async (index: number) => {
      // if the same number is selected, do nothing
      if (index === slide) return;

      // Fade out the current slide
      await fadeElement(slide, false);

      // Set the new slide and fade it in
      setSlide(index);
      fadeElement(index, true);
    },
    [slide, fadeElement, setSlide],
  );

  return { showElement, setRef, slide };
}

export default useActiveElement;
